/**
 * Discover Contract URL Utilities
 * URL sync for filter state
 */
import type { DiscoverFilters } from './types';
import { DEFAULT_FILTERS } from './types';
import { parseFiltersFromSearchParams } from './schema';

/**
 * Canonical committed URL state shared by every Discover surface.
 *
 * Taxonomy values are written as repeated parameters so React Router's
 * `getAll()` readers, public Katalag feeds, and both desktop filter panels
 * observe the exact same state. Null range values intentionally clear a key.
 */
export type DiscoverViewVariant = 'card' | 'list' | 'reel';

export interface DiscoverUrlState {
  q?: string | null;
  industry_ids?: string[] | null;
  category_ids?: string[] | null;
  subcategory_ids?: string[] | null;
  hashtag_ids?: string[] | null;
  price_min?: number | null;
  price_max?: number | null;
  commission_min?: number | null;
  commission_max?: number | null;
  discount_min?: number | null;
  discount_max?: number | null;
  view?: DiscoverViewVariant | null;
}

export interface DiscoverDraftState {
  readonly kind: 'draft';
  value: DiscoverUrlState;
}

export interface DiscoverAppliedState {
  readonly kind: 'applied';
  value: DiscoverUrlState;
}

function appendRepeatedParam(
  params: URLSearchParams,
  key: string,
  values: readonly string[] | null | undefined,
): void {
  values?.forEach((value) => {
    const normalized = value.trim();
    if (normalized) params.append(key, normalized);
  });
}

function appendFiniteNumber(
  params: URLSearchParams,
  key: string,
  value: number | null | undefined,
): void {
  if (value != null && Number.isFinite(value)) {
    params.set(key, String(value));
  }
}

/**
 * Serialize the live Discover search/filter state with one deterministic
 * writer. This is intentionally narrower than `filtersToSearchParams`, which
 * also supports legacy v2 filters, sorting, and pagination.
 */
export function discoverUrlStateToSearchParams(state: DiscoverUrlState): URLSearchParams {
  const params = new URLSearchParams();
  const query = state.q?.trim();

  if (query) params.set('q', query);

  appendRepeatedParam(params, 'industry_ids', state.industry_ids);
  appendRepeatedParam(params, 'category_ids', state.category_ids);
  appendRepeatedParam(params, 'subcategory_ids', state.subcategory_ids);
  appendRepeatedParam(params, 'hashtag_ids', state.hashtag_ids);

  appendFiniteNumber(params, 'price_min', state.price_min);
  appendFiniteNumber(params, 'price_max', state.price_max);
  appendFiniteNumber(params, 'commission_min', state.commission_min);
  appendFiniteNumber(params, 'commission_max', state.commission_max);
  appendFiniteNumber(params, 'discount_min', state.discount_min);
  appendFiniteNumber(params, 'discount_max', state.discount_max);

  if (state.view) params.set('view', state.view);

  return params;
}

function normalizedArray(values: readonly string[] | null | undefined): string[] | undefined {
  const normalized = values?.map((value) => value.trim()).filter((value) => value.length > 0) ?? [];

  return normalized.length > 0 ? normalized : undefined;
}

function finiteNumberFromParam(params: URLSearchParams, key: string): number | undefined {
  const raw = params.get(key);
  if (raw == null || raw.trim() === '') return undefined;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function copyDiscoverUrlState(state: DiscoverUrlState): DiscoverUrlState {
  return {
    ...state,
    industry_ids: normalizedArray(state.industry_ids),
    category_ids: normalizedArray(state.category_ids),
    subcategory_ids: normalizedArray(state.subcategory_ids),
    hashtag_ids: normalizedArray(state.hashtag_ids),
  };
}

/**
 * Parse the canonical Discover URL state.
 * Legacy `grid` input is accepted only here and normalized to `card`.
 */
export function searchParamsToDiscoverUrlState(params: URLSearchParams): DiscoverUrlState {
  const query = params.get('q')?.trim();
  const rawView = params.get('view');

  const view: DiscoverViewVariant | undefined =
    rawView === 'grid'
      ? 'card'
      : rawView === 'card' || rawView === 'list' || rawView === 'reel'
        ? rawView
        : undefined;

  return {
    ...(query ? { q: query } : {}),
    ...(normalizedArray(params.getAll('industry_ids'))
      ? { industry_ids: normalizedArray(params.getAll('industry_ids')) }
      : {}),
    ...(normalizedArray(params.getAll('category_ids'))
      ? { category_ids: normalizedArray(params.getAll('category_ids')) }
      : {}),
    ...(normalizedArray(params.getAll('subcategory_ids'))
      ? {
          subcategory_ids: normalizedArray(params.getAll('subcategory_ids')),
        }
      : {}),
    ...(normalizedArray(params.getAll('hashtag_ids'))
      ? { hashtag_ids: normalizedArray(params.getAll('hashtag_ids')) }
      : {}),
    ...(finiteNumberFromParam(params, 'price_min') !== undefined
      ? { price_min: finiteNumberFromParam(params, 'price_min') }
      : {}),
    ...(finiteNumberFromParam(params, 'price_max') !== undefined
      ? { price_max: finiteNumberFromParam(params, 'price_max') }
      : {}),
    ...(finiteNumberFromParam(params, 'commission_min') !== undefined
      ? {
          commission_min: finiteNumberFromParam(params, 'commission_min'),
        }
      : {}),
    ...(finiteNumberFromParam(params, 'commission_max') !== undefined
      ? {
          commission_max: finiteNumberFromParam(params, 'commission_max'),
        }
      : {}),
    ...(finiteNumberFromParam(params, 'discount_min') !== undefined
      ? {
          discount_min: finiteNumberFromParam(params, 'discount_min'),
        }
      : {}),
    ...(finiteNumberFromParam(params, 'discount_max') !== undefined
      ? {
          discount_max: finiteNumberFromParam(params, 'discount_max'),
        }
      : {}),
    ...(view ? { view } : {}),
  };
}

export function createDiscoverDraftState(
  applied: DiscoverUrlState | DiscoverAppliedState,
): DiscoverDraftState {
  const value = 'kind' in applied ? applied.value : applied;
  return {
    kind: 'draft',
    value: copyDiscoverUrlState(value),
  };
}

export function createDiscoverAppliedState(value: DiscoverUrlState): DiscoverAppliedState {
  return {
    kind: 'applied',
    value: copyDiscoverUrlState(value),
  };
}

/**
 * Apply commits Filter state only. Search and presentation view remain
 * at their previously applied values.
 */
export function applyDiscoverDraftFilters(
  draft: DiscoverDraftState,
  applied: DiscoverAppliedState,
): DiscoverAppliedState {
  return createDiscoverAppliedState({
    ...applied.value,
    industry_ids: normalizedArray(draft.value.industry_ids),
    category_ids: normalizedArray(draft.value.category_ids),
    subcategory_ids: normalizedArray(draft.value.subcategory_ids),
    hashtag_ids: normalizedArray(draft.value.hashtag_ids),
    price_min: draft.value.price_min,
    price_max: draft.value.price_max,
    commission_min: draft.value.commission_min,
    commission_max: draft.value.commission_max,
    discount_min: draft.value.discount_min,
    discount_max: draft.value.discount_max,
  });
}

/**
 * Explicit Search commits Search state only. Applied Filters are retained.
 */
export function commitDiscoverDraftSearch(
  draft: DiscoverDraftState,
  applied: DiscoverAppliedState,
): DiscoverAppliedState {
  return createDiscoverAppliedState({
    ...applied.value,
    q: draft.value.q?.trim() || undefined,
  });
}

export function clearDiscoverDraftAndApplied(): {
  draft: DiscoverDraftState;
  applied: DiscoverAppliedState;
} {
  const applied = createDiscoverAppliedState({});
  return {
    applied,
    draft: createDiscoverDraftState(applied),
  };
}

export function countAppliedDiscoverFilters(applied: DiscoverAppliedState): number {
  const value = applied.value;

  const arrayCount =
    (value.industry_ids?.length ?? 0) +
    (value.category_ids?.length ?? 0) +
    (value.subcategory_ids?.length ?? 0) +
    (value.hashtag_ids?.length ?? 0);

  const numericCount = [
    value.price_min,
    value.price_max,
    value.commission_min,
    value.commission_max,
    value.discount_min,
    value.discount_max,
  ].filter((numberValue) => numberValue != null && Number.isFinite(numberValue)).length;

  return arrayCount + numericCount;
}

/**
 * One atomic committed URL writer.
 */
export function discoverAppliedStateToSearchParams(applied: DiscoverAppliedState): URLSearchParams {
  return discoverUrlStateToSearchParams(applied.value);
}

/**
 * Convert filters to URLSearchParams
 * Only includes non-default values
 */
export function filtersToSearchParams(filters: DiscoverFilters): URLSearchParams {
  const params = new URLSearchParams();

  // Text search
  if (filters.q && filters.q.trim()) {
    params.set('q', filters.q.trim());
  }

  // Array filters (only if non-empty)
  if (filters.subjects.length > 0) {
    params.set('subjects', filters.subjects.join(','));
  }
  if (filters.geo.length > 0) {
    params.set('geo', filters.geo.join(','));
  }
  if (filters.brand_categories.length > 0) {
    params.set('brand_categories', filters.brand_categories.join(','));
  }
  if (filters.psychographics.length > 0) {
    params.set('psychographics', filters.psychographics.join(','));
  }
  if (filters.brand_geographic_focus.length > 0) {
    params.set('brand_geographic_focus', filters.brand_geographic_focus.join(','));
  }

  // V3 taxonomy filters
  if (filters.industry_ids && filters.industry_ids.length > 0) {
    params.set('industry_ids', filters.industry_ids.join(','));
  }
  if (filters.category_ids && filters.category_ids.length > 0) {
    params.set('category_ids', filters.category_ids.join(','));
  }
  if (filters.subcategory_ids && filters.subcategory_ids.length > 0) {
    params.set('subcategory_ids', filters.subcategory_ids.join(','));
  }
  if (filters.hashtag_ids && filters.hashtag_ids.length > 0) {
    params.set('hashtag_ids', filters.hashtag_ids.join(','));
  }

  // Range filters
  if (filters.price_min != null) params.set('price_min', String(filters.price_min));
  if (filters.price_max != null) params.set('price_max', String(filters.price_max));
  if (filters.discount_min != null) params.set('discount_min', String(filters.discount_min));
  if (filters.discount_max != null) params.set('discount_max', String(filters.discount_max));
  if (filters.commission_min != null) params.set('commission_min', String(filters.commission_min));
  if (filters.commission_max != null) params.set('commission_max', String(filters.commission_max));

  // Sort (only if not default)
  if (filters.sort !== 'relevance') {
    params.set('sort', filters.sort);
  }

  // Page (only if not first page)
  if (filters.page > 1) {
    params.set('page', String(filters.page));
  }

  // Page size (only if not default)
  if (filters.pageSize !== 24) {
    params.set('pageSize', String(filters.pageSize));
  }

  return params;
}

/**
 * Convert URLSearchParams to filters
 */
export function searchParamsToFilters(params: URLSearchParams): DiscoverFilters {
  return parseFiltersFromSearchParams(params);
}

/**
 * Build URL with filters applied
 */
export function buildUrlWithFilters(baseUrl: string, filters: DiscoverFilters): string {
  const params = filtersToSearchParams(filters);
  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * Reset filters to defaults
 */
export function resetFilters(): DiscoverFilters {
  return { ...DEFAULT_FILTERS };
}

/**
 * Update a single filter value immutably
 */
export function updateFilter<K extends keyof DiscoverFilters>(
  filters: DiscoverFilters,
  key: K,
  value: DiscoverFilters[K],
): DiscoverFilters {
  return { ...filters, [key]: value, page: key === 'page' ? (value as number) : 1 };
}

/**
 * Toggle a value in an array filter (supports both v2 and v3 filter keys)
 */
export function toggleArrayFilter(
  filters: DiscoverFilters,
  key:
    | 'subjects'
    | 'geo'
    | 'brand_categories'
    | 'psychographics'
    | 'brand_geographic_focus'
    | 'industry_ids'
    | 'category_ids'
    | 'subcategory_ids'
    | 'hashtag_ids',
  value: string,
): DiscoverFilters {
  const current = filters[key] || [];
  const updated = current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];

  // For v3 taxonomy: clear child selections when parent changes
  let result: DiscoverFilters = { ...filters, [key]: updated, page: 1 };

  // Progressive disclosure: clear children when parent filter changes
  if (key === 'industry_ids') {
    // Changing industry clears category and subcategory
    result = { ...result, category_ids: [], subcategory_ids: [] };
  } else if (key === 'category_ids') {
    // Changing category clears subcategory
    result = { ...result, subcategory_ids: [] };
  }

  return result;
}

/**
 * Clear v3 taxonomy filters while keeping v2 filters
 */
export function clearTaxonomyFilters(filters: DiscoverFilters): DiscoverFilters {
  return {
    ...filters,
    industry_ids: [],
    category_ids: [],
    subcategory_ids: [],
    page: 1,
  };
}
